// ja.ts — Japanese catalog for the owner dashboard.
//
// Translated per docs/superpowers/specs/locale-translator-contract.md (binding tone, do-not-
// translate list, plural rule, glyphs). `satisfies Catalog` is the proof every key was
// translated, not just typed.
//
// Function-valued entries keep their exact parameter names and interpolate every argument they
// need; Japanese has no plural inflection, so every `n === 1 ? 'x' : 'xs'` ternary in en.ts
// collapses to one fixed form here — the parameter itself is still threaded through the
// signature even where it no longer drives a branch.
import { type Catalog } from './en';

export const ja = {
  common: {
    close: '閉じる',
  },
  status: {
    pending: '待機中',
    crawling: 'クロール中',
    indexing: 'インデックス中',
    ready: '準備完了',
    error: 'エラー',
    paused: '一時停止',
    hint: 'ヒント',
    deleting: '削除中',
  },
  time: {
    never: 'なし',
    justNow: 'たった今',
    inAMoment: 'まもなく',
    secondsAgo: ({ n }: { n: number }) => `${n}秒前`,
    inSeconds: ({ n }: { n: number }) => `${n}秒後`,
    minutesAgo: ({ n }: { n: number }) => `${n}分前`,
    inMinutes: ({ n }: { n: number }) => `${n}分後`,
    hoursAgo: ({ n }: { n: number }) => `${n}時間前`,
    inHours: ({ n }: { n: number }) => `${n}時間後`,
    daysAgo: ({ n }: { n: number }) => `${n}日前`,
    inDays: ({ n }: { n: number }) => `${n}日後`,
    monthsAgo: ({ n }: { n: number }) => `${n}か月前`,
    inMonths: ({ n }: { n: number }) => `${n}か月後`,
    yearsAgo: ({ n }: { n: number }) => `${n}年前`,
    inYears: ({ n }: { n: number }) => `${n}年後`,
  },
  shell: {
    impersonating: ({ email }: { email: string }) =>
      `${email} として閲覧中 — このユーザーになりすましています。`,
    stopImpersonating: '停止',

    platformAdmin: 'プラットフォーム管理',
    adminTabWorkspaces: 'ワークスペース',
    adminTabBots: 'ボット',
    adminTabJobs: 'ジョブ',
    adminTabPosts: '投稿',
    adminTabFeedback: 'フィードバック',
    adminTabPlayground: 'Playground',

    botsLoadError: 'ボットを読み込めませんでした。',
    botsLoadErrorInline: 'ボットを読み込めませんでした',
    reloadToRetry: 'ページを再読み込みしてもう一度お試しください。',

    navOverview: '概要',
    navUnanswered: '未回答',
    navConversations: '会話',
    navLeads: 'リード',
    navSources: 'ソース',
    navPlayground: 'Playground',
    navAppearance: '外観',
    navSettings: '設定',
    groupNeedsYou: '対応が必要',
    groupKnowledge: 'ナレッジ',
    groupSetup: 'セットアップ',

    closeMenu: 'メニューを閉じる',
    navigationLabel: 'ナビゲーション',
    openNavigation: 'ナビゲーションを開く',
    openAccountMenu: 'アカウントメニューを開く',

    switchBotAriaLabel: ({ name }: { name: string }) => `${name}、ボットを切り替え`,
    botSwitcherHeader: ({ n, cap }: { n: number; cap: number | null }) =>
      `ボット一覧${cap !== null ? ` · ${n}/${cap}使用中` : ''}`,
    allBots: 'すべてのボット',
    newBot: '新しいボット',
    newBotOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `新しいボット — ${n}/${cap}使用中`,

    pagesIndexed: 'インデックス済みページ',

    feedbackTrigger: 'フィードバック',
    feedbackTypeGroupLabel: 'フィードバックの種類',
    feedbackTypeBug: '不具合',
    feedbackTypeFeature: '機能リクエスト',
    feedbackTypeQuestion: '質問',
    feedbackPlaceholder: '何が起きましたか?どうすればEmbentがもっと良くなりますか?',
    feedbackSent: '送信しました — すべて目を通しています。',
    feedbackSendError: 'フィードバックを送信できませんでした。',
    feedbackSending: '送信中…',
    feedbackSend: '送信',
    feedbackDialogTitle: 'フィードバックを送る',
    feedbackDialogDescription: '不具合、機能リクエスト、質問など — 内容はそのままチームに届きます。',

    workspaceSettings: 'ワークスペース設定',
    members: 'メンバー',
    billingUsage: '請求と利用状況',
    switchWorkspace: 'ワークスペースを切り替え',
    accountLabelPrefix: 'アカウント — ',
    account: 'アカウント',
    signOut: 'サインアウト',
    themePrefix: 'テーマ: ',
    languagePrefix: '言語: ',
    nextPrefix: '次: ',
    themeSystem: 'システム',
    themeDark: 'ダーク',
    themeLight: 'ライト',
    roleOwner: 'オーナー',
    roleAdmin: '管理者',
    roleMember: 'メンバー',
    workspaceSubline: ({ plan, role }: { plan: string | null; role: string }) =>
      plan ? `${plan} · あなたは${role}` : `あなたは${role}`,
    workspaceMenuAriaLabel: ({ name }: { name: string }) => `${name}、ワークスペースとアカウントのメニュー`,

    usageWarning: ({ pct }: { pct: number }) =>
      `月間メッセージの${pct}%を使用しました — アップグレードすればすべての会話を止めずに続けられます。`,
    dismiss: '閉じる',

    renewsOn: ({ date }: { date: string }) => `更新日 ${date}`,
    resetsOn: ({ date }: { date: string }) => `リセット日 ${date}`,
    answersPaused: '新しい回答を一時停止中 — 上限の110%を超過',
    messagesLeft: ({ n }: { n: string }) => `今月あと${n}件のメッセージ`,
    badgeStillShown: ({ plan }: { plan: string }) => `${plan}では「Powered by Embent」バッジが表示されます`,
    badgeOptional: ({ plan }: { plan: string }) => `${plan}では「Powered by Embent」バッジを非表示にできます`,
    upgradeCta: ({ price }: { price: number }) => `アップグレード — $${price}/月`,
    resumeAnswersCta: ({ price }: { price: number }) => `回答を再開 — $${price}/月`,
    resumeAnswersBenefit: 'アップグレードすると回答がすぐに再開します',
    upgradeBenefit: ({
      plan,
      messages,
      bots,
      seats,
    }: {
      plan: string;
      messages: string;
      bots: number;
      seats: number;
    }) => `${plan}なら、メッセージ${messages}件・ボット${bots}台・${seats}シートが追加されます`,
  },
  bots: {
    noSources: 'ソースなし',
    allSourcesReady: ({ n }: { n: number }) => `${n} · すべて準備完了`,
    sourceStatusRollup: ({ n, status }: { n: number; status: string }) => `${n} · ${status}`,
    paused: '一時停止',
    sourcesRemoving: ({ n }: { n: number }) => `ソース${n}件 · 削除中`,
    sourcesSyncError: ({ n }: { n: number }) => `ソース${n}件 · 同期エラー`,
    sourcesSyncing: ({ n }: { n: number }) => `ソース${n}件 · 同期中`,
    messagesUnanswered: ({ messages, unanswered }: { messages: number; unanswered: number }) =>
      `メッセージ${messages}件 · 未回答${unanswered}件`,
    botsCount: ({ n }: { n: number }) => `ボット${n}台`,
    botsOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `${n}/${cap}使用中`,

    colBot: 'ボット',
    colStatus: 'ステータス',
    colMessages14d: 'メッセージ · 14日間',
    colUnanswered: '未回答',
    colSources: 'ソース',
    pageTitle: 'ボット',
    emptyTitle: '最初のボットを作成',
    emptyDescription: 'サイトやドキュメントを設定すれば、コンテンツをクロールして訪問者の質問に答え始めます。',
    planIncludesBots: ({ cap }: { cap: number }) => `現在のプランにはボット${cap}台が含まれます`,
    upgradeGateDescription: ({ name, priceUsd, maxBots }: { name: string; priceUsd: number; maxBots: number }) =>
      `${name}($${priceUsd}/月)なら最大${maxBots}台のボットを運用できます — サイトごとに1台、それぞれ独自のナレッジと見た目を持てます。`,
    comparePlansLink: 'プランを比較 →',
    newBotDialogTitle: '新しいボット',
    botNameFieldLabel: '名前',
    botNamePlaceholder: 'Acme サポートボット',
    creatingCta: '作成中…',
    createCta: '作成',
  },
  auth: {
    emailLabel: 'メールアドレス',
    passwordLabel: 'パスワード',
    genericFormError: '問題が発生しました。もう一度お試しください。',
    continueWithGoogle: 'Googleで続ける',
    backToSignIn: 'サインインに戻る',

    signInTitle: 'サインイン',
    signInSubtitle: 'おかえりなさい。',
    passwordJustResetNotice: 'パスワードを更新しました — 新しいパスワードでサインインしてください。',
    forgotPasswordLink: 'パスワードをお忘れですか?',
    unverifiedResendNotice: '受信箱をご確認ください — 確認メールを再送しました。',
    signingIn: 'サインイン中…',
    signInCta: 'サインイン',
    noAccountPrompt: 'アカウントをお持ちでない場合は ',
    signUpLink: '新規登録',

    signUpTitle: 'アカウントを作成',
    signUpSubtitle: '数分で訪問者への回答を始められます。',
    nameLabel: '名前',
    creatingAccount: 'アカウントを作成中…',
    createAccountCta: 'アカウントを作成',
    haveAccountPrompt: 'すでにアカウントをお持ちの場合は ',
    signInLink: 'サインイン',

    checkEmailTitle: 'メールをご確認ください',
    checkEmailSubtitle: '確認用リンクを送信しました。通常1分以内に届きます。',
    checkEmailBody: 'メール内のリンクをクリックしてアカウントを有効化してください。',

    verifyFailedTitle: '確認に失敗しました',
    verifiedTitle: 'メールアドレスを確認しました',
    verifyFailedBody: 'この確認リンクは無効か、有効期限が切れています。',
    verifiedBody: '準備が整いました — ダッシュボードに移動します…',

    requestResetTitle: 'パスワードをリセット',
    requestResetSubtitlePending: 'メールアドレスを入力すると、リセット用リンクをお送りします。',
    requestResetSubtitleSent: 'そのメールアドレスにEmbentアカウントがあれば、リセット用リンクを送信しました。',
    requestResetBodySent: '受信箱をご確認ください。リンクの有効期限は1時間です。',
    sendingResetLink: '送信中…',
    sendResetLinkCta: 'リセットリンクを送信',

    resetLinkInvalidTitle: 'リセットリンクが無効です',
    resetLinkInvalidBody: 'このパスワードリセットリンクにはトークンがないか、すでに使用されています。',
    requestNewLinkCta: '新しいリンクをリクエスト',
    chooseNewPasswordTitle: '新しいパスワードを設定',
    chooseNewPasswordSubtitle: '以前使ったことのないものにしてください。',
    newPasswordLabel: '新しいパスワード',
    resetLinkExpiredFallback: 'このリセットリンクは無効か、有効期限が切れています。',
    savingPassword: '保存中…',
    saveNewPasswordCta: '新しいパスワードを保存',

    inviteNotValidTitle: '招待が無効です',
    goToEmbentLink: 'Embentへ移動',
    inviteInvalidFallback: 'この招待は無効になっています。',
    inviteTitle: ({ workspaceName }: { workspaceName: string }) => `${workspaceName}に招待されています`,
    inviteDescription: ({ email }: { email: string }) =>
      `${email} 宛に送信されました — そのメールアドレスでサインインまたはアカウントを作成すると招待を承認できます。`,
    inviteSignInCta: 'サインイン',
    inviteSignUpCta: 'アカウントを作成',
    wrongAccountTitle: 'アカウントが違います',
    signOutCta: 'サインアウト',
    inviteAcceptFallback: 'この招待の承認中に問題が発生しました。',
    joiningWorkspaceTitle: ({ workspaceName }: { workspaceName: string }) => `${workspaceName}に参加中…`,
    joiningWorkspaceBody: '少々お待ちください。',

    eventLabels: {
      newLead: '新しいリード',
      unansweredDigest: '未回答ダイジェスト',
      crawlFailed: 'クロール失敗',
      weeklySummary: '週次サマリー',
    },
    unsubscribeLinkExpiredTitle: 'このリンクは期限切れです',
    unsubscribeLinkExpiredBody: '通知の設定は、ダッシュボードのボット設定から変更できます。',
    openDashboardCta: 'ダッシュボードを開く',
    unsubscribedTitle: '配信を停止しました',
    unsubscribedBody: ({ eventLabel, botName }: { eventLabel: string; botName: string | null }) =>
      `今後、${botName ?? 'このボット'}の${eventLabel}メールは届きません。ボットの設定からいつでも再度オンにできます。`,

    notFoundEyebrow: 'ページが見つかりません',
    notFoundTitle: 'このアドレスには何もありません',
    notFoundBody: 'リンクが古いか、ページが移動した可能性があります。',
    notFoundCta: 'ボット一覧へ',

    landingBotsErrorFallback: 'ボットの読み込み中に問題が発生しました。',
    landingGoToBotsCta: 'ボット一覧へ →',
  },
  onboarding: {
    skipForNow: '今はスキップ',
    invalidWebsiteUrl: '有効なウェブサイトのURLを入力してください。例: https://acme.vn',
    websiteUrlLabel: 'ウェブサイトのURL',
    genericFallbackError: '問題が発生しました。',

    crawlDefaultHostname: 'あなたのサイト',
    noSourceToReplace: '置き換えるソースがまだありません。',

    stepWebsite: 'サイト',
    stepCrawl: 'クロール',
    stepTryIt: '試す',
    stepLook: '見た目',
    stepGoLive: '公開',
    stepMobileReadout: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `ステップ ${current}/${total} · ${label}`,
    stepProgressAriaLabel: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `オンボーディングの進捗:ステップ${current}/${total}、${label}`,
    stepTablistAriaLabel: 'オンボーディングの進捗',

    websiteHeading: 'サイトを追加',
    websiteSubtitle:
      'Embentがサイトを読み込み、引用付きで回答するボットを作成します — 何も作り話はしません。ほとんどのサイトは数分で準備が整います。',
    botNameLabel: 'ボット名',
    botNamePlaceholder: 'Acme サポート',
    websiteUrlPlaceholder: 'acme.vn',
    websiteUrlHelper: 'ページは自動的に見つけます。ソースは後からいくつでも追加できます。',
    botNameRequired: 'ボットの名前を入力してください。',
    websiteStarting: '開始中…',
    websiteSubmitCta: 'サイトの読み込みを開始',

    crawlHeading: ({ hostname }: { hostname: string }) => `${hostname}を読み込んでいます…`,
    crawlSubtitleReady: '完了しました — すべてのページがインデックスされ、回答できる状態です。',
    crawlSubtitleSyncing: 'ここで待つ必要はありません — 完了を待つ間にボットを試してみてください。',
    crawlStatusLines: {
      pending: ['待機中 — まもなく開始します…', '開始の準備をしています…'],
      crawling: ['サイト内のページを探しています…', 'リンクをたどっています…', 'ページを読み込んでいます…'],
      indexing: ['検索インデックスを作成しています…', '読み込んだ内容を処理しています…'],
    },
    crawlPagesIndexed: ({ n }: { n: number }) => `${n}ページをインデックス済み`,
    crawlNoSourceYet: 'このボットのソースがまだ見つかりません。',
    crawlErrorLead: '多くの場合、アドレスの入力ミスが原因です — 下の欄を確認してもう一度お試しください。',
    crawlReplaceUrlCta: '別のアドレスを試す',
    crawlReplaceUrlUpdating: '更新中…',
    crawlReplaceUrlHelper: '失敗した試行を削除し、このアドレスで最初からやり直します。',
    crawlRetryCta: '再試行',
    crawlRetrying: '再試行中…',
    crawlRetryHelper:
      'アドレスが正しく、サイトが一時的にアクセスできなかっただけであれば、再試行してください。表示に' +
      'JavaScriptが必要なサイトは、セットアップ後にSourcesの画面で使える設定で対応できます。',
    crawlCtaSyncing: '完了を待つ間にボットを試す →',
    crawlCtaReady: '次へ:ボットを試す →',

    tryItHeading: '何でも聞いてみましょう',
    tryItSubtitleWithPages: ({ n }: { n: number }) =>
      `すでに${n}ページを把握済みです — 残りも引き続き学習中です。すべての回答にソースが表示されます。`,
    tryItSubtitleNoPages: 'まだサイトを読み込んでいます — とりあえず質問してみてください。回答はこれからどんどん良くなっていきます。',
    tryItSamplePrompt: `例:「返品ポリシーは?」`,
    tryItPlaceholder: '配送、料金など、何でも聞いてください…',
    tryItSending: '送信中…',
    tryItSendCta: '送信',
    tryItNextCta: '良さそうです — 自分のものにする',

    lookHeading: 'サイトに合わせて調整',
    lookSubtitle: 'ブランドカラーを選んでください — 入力に合わせてプレビューが更新されます。細かい調整は後から外観画面で行えます。',
    brandColorLabel: 'ブランドカラー',
    colorHexAriaLabel: 'カラーコード(16進数)',
    welcomeMessageLabel: 'ウェルカムメッセージ',
    lookSaving: '保存中…',
    lookContinueCta: '次へ',

    goLiveHeading: 'スニペット1つで公開完了',
    goLivePasteBeforePrefix: 'これを閉じる',
    goLivePasteBeforeSuffix: 'タグの直前にサイトに貼り付けてください。それだけで完了です — ボットが画面右下に表示されます。',
    goLiveInstallGuidesLink: 'プラットフォーム別の導入ガイド',
    goLiveFirstAnswerNotice: 'ウィジェットが最初の訪問者に回答した瞬間、メールでお知らせします。',
    goLiveDashboardCta: 'ダッシュボードへ',
    goLiveViewSourcesCta: 'ソースを見る',
  },
  overview: {
    title: '概要',
    liveOnPrefix: '公開中: ',
    answeringSince: ({ date }: { date: string }) => `${date}から回答中`,
    testInPlaygroundCta: 'Playgroundで試す',
    addSourcesCta: 'ソースを追加',
    botDetailsLoadError: ({ message }: { message: string }) => `ボットの詳細を読み込めませんでした — ${message}`,

    messagesLabel: 'メッセージ · 過去14日間',
    conversationsLink: '会話 →',
    vsPreviousPeriod: '前の14日間との比較',
    leadsLabel: '獲得したリード',
    newLeadsCount: ({ n }: { n: number }) => `新規${n}件`,
    leadsThisWeek: ({ n }: { n: number }) => `今週${n}件`,
    viewLeadsLink: 'リードを見る →',
    unansweredLabel: '未回答の質問',
    fixThemLink: '対応する →',

    knowledgeHeading: 'ナレッジ',
    pointAtSite: 'サイトを設定',
    addSourceLink: 'ソースを追加 →',
    addSourcesLink: 'ソースを追加 →',
    pagesIndexed: 'インデックス済みページ',
    sourcesCountLabel: 'ソース',
    lastSyncedLabel: '最終同期',
    manageSourcesLink: 'ソースを管理 →',
    resyncing: '再同期中…',
    allSyncing: 'すべて同期中',
    resyncCta: '再同期',
    resyncNothingToSync: '同期するものがありません — すべてのソースがすでに同期中です。',
    resyncGenericError: '再同期を開始できませんでした — もう一度お試しください。',

    latestConversationsHeading: '最近の会話',
    allConversationsLink: 'すべての会話 →',
    noConversationsEmpty: '過去14日間は動きがありません — 新しい会話はここに表示されます。',
    noMessageFallback: '(メッセージなし)',
    leadCapturedTag: 'リード獲得',
    hasUnansweredTag: '未回答あり',

    knowledgeAllReady: 'すべて準備完了',
    knowledgeSyncing: 'ソース同期中',
    knowledgeNeedsAttention: '対応が必要なソースがあります',
    knowledgeRemoving: 'ソースを削除中',

    createdSubtitle: ({ time }: { time: string }) => `作成日時 ${time}`,
    zeroStatMessagesHint: '最初の質問から開始',
    zeroStatLeadsHint: '会話の途中で名前とメールを取得',
    zeroStatUnansweredHint: 'まだ対応することはありません',
    heroReadyHeadline: 'ボットは回答できる状態です',
    heroIndexingHeadline: 'まだサイトを読み込んでいます',
    heroEmptyHeadline: 'ソースを追加して始めましょう',
    heroEmptyBody: 'まだ読み込む内容がありません。ソースを追加すれば、数分で回答できるようになります。',
    heroIndexingErrorBody:
      '対応が必要なソースがあります — このままでは自動的には完了しません。ナレッジカードで問題を確認し、修正したらサイトに設置してください。',
    heroIndexingPrefix: 'まだ読み込み中です: ',
    heroIndexingNoSourceFallback: 'ソース',
    heroIndexingSuffix: ' — 数分後にもう一度確認し、準備ができたらサイトに設置してください。',
    heroReadyBodyPrefix: ({ n }: { n: number }) => `${n}ページを読み込みました`,
    heroReadyFromDomain: '(',
    heroReadyBodySuffix:
      'より) — 質問に答えられるくらいによく理解しています。あと1つのステップ:サイトに設置するだけです。',
    onlyPagesNudge: ({ n }: { n: number }) =>
      `${n}ページだけですか?FAQ、料金、ポリシーのページも追加すると、より多くの情報を活用できます。`,
    addSourceCta: 'ソースを追加',
    addSourceHelperText: '1分ほどで完了します。回答できる内容ができ次第、埋め込みスニペットがここに表示されます。',
    pasteBeforeBodyLabel: '</BODY>の直前に貼り付け',
    emailSnippetLink: 'スニペットを開発担当者にメールで送る',
    emailSnippetSubject: ({ botName }: { botName: string }) => `${botName}をサイトに埋め込む`,
    emailSnippetBody: ({ snippet }: { snippet: string }) =>
      `閉じる</body>タグの直前にこれを貼り付けてください:\n\n${snippet}`,
    whatHappensNextHeading: 'この後の流れ',
    stepVisitorAsksTitle: '訪問者が質問する',
    stepVisitorAsksBody: 'スニペットを設置したすべてのページの右下にアイコンが表示されます。他に設定は不要です。',
    stepEmailAnswerTitle: '最初の回答をメールでお知らせします',
    stepEmailAnswerBody: '誰よりも先に、お客様の言葉そのままで、ボットが何を答えたかを確認できます。',
    stepUnansweredTitle: '答えられなかった質問はここに届きます',
    stepUnansweredBody: '作り話をするくらいなら、正直に答えられないと言います。それぞれの未回答は、あなたが対応すべき1件として記録されます。',
    stepAddSourceTitle: 'ソースを追加',
    stepAddSourceBody: 'サイトを設定するか、ファイルやテキストをアップロードしてください — 回答するには、まず読み込む内容が必要です。',
    stepAddSourceVisitorTitle: '訪問者が質問すると、回答がメールで届きます',
    countersHeading: '最初の訪問者からカウント開始',

    needsYouHeading: '対応が必要',
    allUnansweredLink: 'すべての未回答 →',
    needsYouEmpty: '対応することはありません — ボットはすべての質問に回答済みです。',
    needsYouIntro: 'ボットが「わかりません」と答えた質問です。一度答えれば、以後ずっと覚えています。',
    askedSummary: ({ n, time }: { n: number; time: string }) => `${n}回質問 · 最終 ${time}`,
    writeAnswerCta: '回答を書く',
    cancelCta: 'キャンセル',
    answerPlaceholder: 'この訪問者に返す回答を書いてください。以後、ボットがこの内容を使用します。',
    addingCta: '追加中…',
    addAsQaSourceCta: 'Q&Aソースとして追加',
    saveAnswerError: 'この回答を保存できませんでした — もう一度お試しください。',
  },
  sources: {
    pageTitle: 'ソース',
    sourcesSubtitle: ({
      n,
      pageCount,
      lastSynced,
    }: {
      n: number;
      pageCount: number;
      lastSynced: string;
    }) => `ソース${n}件 · ${pageCount}ページ · 最終同期 ${lastSynced}`,
    botLoadError: ({ message }: { message: string }) => `このボットを読み込めませんでした — ${message}`,

    typeUrl: 'URL',
    typeSitemap: 'サイトマップ',
    typeText: 'テキスト',
    typeFile: 'ファイル',

    colType: '種類',
    colUri: 'URI',
    colStatus: 'ステータス',
    colRenderedMode: 'レンダリングモード',
    colPages: 'ページ',
    colLastSynced: '最終同期',
    colActions: '操作',

    urlFieldLabel: 'ページURL',
    urlPlaceholder: 'https://example.com/docs',
    sitemapFieldLabel: 'サイトマップURL',
    sitemapPlaceholder: 'https://example.com/sitemap.xml',
    textTitleFieldLabel: 'タイトル',
    textTitlePlaceholder: '返品ポリシー',
    textContentFieldLabel: '内容',
    textContentPlaceholder: 'ボットに覚えさせたいテキストを貼り付けてください…',
    fileFieldLabel: 'ファイル',
    fileDropPrompt: 'ここにファイルをドロップ、またはクリックして選択',
    fileDropCaption: ({ extensions, maxSize }: { extensions: string; maxSize: string }) =>
      `${extensions} · 最大${maxSize}`,
    fileTooLarge: ({ maxSize }: { maxSize: string }) => `このファイルは上限の${maxSize}を超えています。`,
    fileTypeNotSupported: ({ ext, extensions }: { ext: string; extensions: string }) =>
      `${ext ? `.${ext}` : 'このファイル'}には対応していません — ${extensions}をお使いください。`,
    filesDroppedNote: ({ name, count }: { name: string; count: number }) =>
      `「${name}」を使用します — 他に${count}件のファイルはドロップされましたが無視されます。`,
    fileDropNotAFile: 'ファイルではありませんでした — パソコンからファイルをドラッグしてください。',
    fileTitleFieldLabel: 'タイトル(任意)',
    fileTitlePlaceholder: '未入力の場合はファイル名を使用',

    addingCta: '追加中…',
    addSourceCta: 'ソースを追加',

    emptyTitle: 'サイトを設定',
    emptyDescription: '上でURL、サイトマップ、テキスト、ファイルのいずれかを追加すると、読み込みを開始します。',

    reviewPagesLink: 'ページを確認',
    nearEmptyInfoAriaLabel: ({ count }: { count: number }) => `${count}ページがほぼ空でした`,

    resyncCta: '再同期',
    deleteCta: '削除',
    cancelCta: 'キャンセル',
    syncInProgressError: '同期処理中です — しばらくしてから再度お試しください',

    deleteConfirmTitle: 'このソースを削除しますか?',
    deleteConfirmDescriptionPrefix: '削除対象: ',
    deleteConfirmDescriptionSuffix: 'と、そこからインデックスされたすべての内容です。この操作は元に戻せません。',

    renderedModeExplainerAriaLabel: 'レンダリングモードとは?',
    renderedModeExplainerBody1Bold: 'レンダリングモード',
    renderedModeExplainerBody1Rest:
      'は、各ページを実際のブラウザで実行してから読み込みます。そのため、JavaScriptの実行後にしか表示されないコンテンツも取得できます。',
    renderedModeExplainerBody2:
      '通常のサイトではオフのままにしてください — その方が速く、コストも抑えられます。クローラーにページが空に見える場合(シングルページアプリ、ダッシュボードなど、ページを開いた後にテキストを読み込むもの)にオンにしてください。',
    renderedModeExplainerBody3: 'トレードオフ:クロールが遅くなり、1回あたりのページ消費量も増えます。',

    renderedModeLabel: 'レンダリングモード',
    modeRendered: 'レンダリング',
    modeStandard: '標準',
    switchModeTitle: ({ mode }: { mode: string }) => `${mode}モードに切り替えますか?`,
    switchModeDescription: ({ mode }: { mode: string }) =>
      `このソースを${mode}モードで再クロールします。内容が変わったページは再インデックスされ、存在しなくなったページは削除されます — 新しいクロールが完了するまで、現在のインデックスはそのまま利用できます。`,
    switchModeCta: ({ mode }: { mode: string }) => `${mode}モードに切り替え`,
  },
  sourceDetail: {
    crawledPages: ({ n }: { n: number }) => `${n}ページをクロールしました。`,
    crawledPagesWithSkipped: ({ n, failedCount }: { n: number; failedCount: number }) =>
      `${n}ページをクロールしました。${failedCount}ページは読み込めず、スキップされました。`,
    truncatedCapacity: ({ n }: { n: number }) =>
      `${n}ページをクロールしたところで、こちら側のクロール容量が上限に達しました。これは一時的な問題です — 数分待ってからクロールを再実行してください。`,
    truncatedDeadline: ({ n }: { n: number }) =>
      `${n}ページをクロールしたところで、このクロールの制限時間に達しました。`,
    truncatedAttempts: ({ n }: { n: number }) =>
      `${n}ページをクロールしたところで、読み込めないページへの試行回数が上限に達しました。`,
    truncatedPageCap: ({ n }: { n: number }) => `${n}ページをクロールしました — 現在のプランのページ上限までです。`,
    remainingAtLeast: ({ n }: { n: number }) => ` 少なくとも${n}ページがインデックスされませんでした。`,
    remainingNone: ' 一部のページはインデックスされていない可能性があります。',
    crawlFailedGeneric: 'このクロールでは、1ページもインデックスされませんでした。',

    typeUrl: 'URL',
    typeSitemap: 'サイトマップ',
    typeText: 'テキスト',
    typeFile: 'ファイル',

    languagePrefix: '言語: ',

    factType: '種類',
    factRenderedMode: 'レンダリングモード',
    factLastSynced: '最終同期',

    resyncCta: '再同期',
    deleteCta: '削除',

    latestCrawlHeading: '最新のクロール',
    neverCrawledYet: 'このソースはまだクロールされていません。',
    crawlRanInMode: ({ rendered }: { rendered: boolean }) =>
      `このクロールは${rendered ? 'レンダリング' : '標準'}モードで実行されました。`,

    failedUrlsHeading: '失敗したURL',
    failedUrlsCountCaveat: ({ total, shown }: { total: number; shown: number }) =>
      ` — 全${total}件中、最初の${shown}件を表示`,
    offSiteRedirectsHeading: 'サイト外へのリダイレクト',
    offSiteRedirectsMoreCaveat: ' — このリストに表示されている以上にある可能性があります',
    nearEmptyCameBack: ({ n }: { n: number }) => `${n}ページがほぼ内容のない状態で返されました。`,

    pagesIndexedHeading: 'インデックス済みページ',
    nearEmptyOnlyToggle: 'ほぼ空のみ',

    noPagesIndexedYet: 'まだインデックスされたページがありません。',
    colUrl: 'URL',
    colCharacters: '文字数',
    colStatus: 'ステータス',
    nearEmptyBadge: 'ほぼ空',

    indexStateIndexed: 'インデックス済み',
    indexStateIndexing: 'インデックス中…',
    indexStateFailed: '失敗',

    fileNoMarkdownExplainer:
      'アップロードされたファイルそのものとして保存されています — 別のテキストコピーではなく元のファイルを保持しているため、ここにはプレビューできる内容がありません。ファイルはインデックスされ、ボットはそこから回答します。',
    nothingStoredForDocument: 'このドキュメントには何も保存されていません。',

    storedContentHeading: '保存されている内容',
    nothingStoredYet: 'まだ何も保存されていません。',

    deleteDialogTitle: 'このソースを削除しますか?',
    deleteDialogDescriptionPrefix: '削除対象: ',
    deleteDialogDescriptionSuffix: 'と、そこからインデックスされたすべての内容です。この操作は元に戻せません。',
    cancelCta: 'キャンセル',
  },
  conversations: {
    today: '今日',
    yesterday: '昨日',
    yourTesting: '自分のテスト',
    visitor: '訪問者',
    playgroundTag: 'Playground',
    noMessageFallback: '(メッセージなし)',
    leadCapturedTag: 'リード獲得',
    hasUnansweredTag: '未回答あり',
    unansweredChipLabel: '未回答',
    loadingMore: '読み込み中…',
    loadMore: 'もっと見る',
    selectConversationPrompt: '会話を選択すると、やり取りを表示します。',
    backToConversations: '← 会話一覧に戻る',
    transcriptStartedSummary: ({ time, n }: { time: string; n: number }) => `開始 ${time} · メッセージ${n}件`,
    deleteConversationAriaLabel: 'この会話を削除',
    leadSubmittedTag: '✓ リード送信済み',
    cancelCta: 'キャンセル',
    deletingCta: '削除中…',

    pageTitle: '会話',
    headerSubtitle: ({ n }: { n: number }) => `会話${n}件 · 新しい順`,
    emptyTitle: '最初の訪問者を待っています',
    emptyDescription: 'ボットと会話が始まると、ここに表示されます。',

    deleteAllCta: 'すべての会話を削除',
    deleteDialogTitle: 'この会話を削除しますか?',
    deleteDialogDescription:
      'この会話とメッセージを完全に削除します。会話内で獲得したリードはLeadsタブに残ります。この操作は元に戻せません。',
    deleteCta: '会話を削除',

    deleteAllDialogTitle: 'すべての会話を削除しますか?',
    deleteAllDescriptionPrefix:
      'このボットのすべての会話とメッセージを完全に削除します。獲得済みのリードはLeadsタブに残ります。この操作は元に戻せません。確認のため ',
    deleteAllDescriptionSuffix: ' と入力してください。',
    confirmBotNameAriaLabel: 'ボット名を確認',
  },
  leads: {
    filterNew: '新規',
    filterContacted: '連絡済み',
    filterArchived: 'アーカイブ済み',
    filterAll: 'すべて',
    actionMarkNew: '新規にする',
    actionMarkContacted: '連絡済みにする',
    actionArchive: 'アーカイブ',
    statusNew: '新規',
    statusContacted: '連絡済み',
    statusArchived: 'アーカイブ済み',

    filterByStatusAriaLabel: 'ステータスで絞り込み',
    pageTitle: 'リード',
    exportingCta: 'エクスポート中…',
    exportCsvCta: 'CSVエクスポート',
    exportCsvError: 'リードをエクスポートできませんでした — もう一度お試しください。',

    headerSubtitleAll: ({ n }: { n: number }) => `リード${n}件を獲得 · 新しい順`,
    headerSubtitleFiltered: ({ n, filterLabel }: { n: number; filterLabel: string }) =>
      `${filterLabel}のリード${n}件 · 新しい順`,

    deleteAllCta: 'すべてのリードを削除',
    bulkActionsAriaLabel: '一括操作',
    selectedCount: ({ n }: { n: number }) => `${n}件選択中`,
    bulkCapMessage: ({ max }: { max: number }) => `一度に選択できるのは最大${max}件です`,
    bulkUpdatedMessage: ({ n }: { n: number }) => `${n}件更新しました`,
    bulkUpdateError: 'これらのリードを更新できませんでした。もう一度お試しください。',

    emptyTitle: '最初のリードを待っています',
    emptyDescription: 'ボットのチャットウィジェットが獲得したリードはここに表示されます。',
    noFilterResultsTitle: ({ filterLabel }: { filterLabel: string }) => `${filterLabel}のリードはありません`,
    noFilterResultsDescription: '現在、このフィルターに該当するものはありません。',

    selectAllAriaLabel: 'すべてのリードを選択',
    colName: '名前',
    colContact: '連絡先',
    colIntent: '意図',
    colStatus: 'ステータス',
    colCaptured: '獲得日時',
    colActions: '操作',

    noEmailFallback: '(メールなし)',
    selectLeadAriaLabel: ({ contact }: { contact: string }) => `リード ${contact} を選択`,
    deleteLeadAriaLabel: ({ contact }: { contact: string }) => `リード ${contact} を削除`,
    playgroundTag: 'Playground',
    viewChatCta: 'チャットを見る',
    loadingMoreCta: '読み込み中…',
    loadMoreCta: 'もっと見る',

    cancelCta: 'キャンセル',
    deletingCta: '削除中…',

    deleteDialogTitle: 'このリードを削除しますか?',
    deleteDescriptionPrefix: '完全に削除します: ',
    deleteDescriptionSuffix: '。この操作は元に戻せません。',
    noContactFallback: 'このリード',
    deleteCta: 'リードを削除',

    deleteAllDialogTitle: 'すべてのリードを削除しますか?',
    deleteAllDescriptionPrefix: 'このボットが獲得したすべてのリードを完全に削除します。この操作は元に戻せません。確認のため ',
    deleteAllDescriptionSuffix: ' と入力してください。',
    confirmBotNameAriaLabel: 'ボット名を確認',
  },
  unanswered: {
    tabOpen: '未対応',
    tabDismissed: '却下済み',
    tabAnswered: '回答済み',
    filterByStatusAriaLabel: 'ステータスで絞り込み',

    pageTitle: '未回答',
    headerSubtitle: ({ n, verb }: { n: number; verb: string }) => `質問${n}件が${verb}`,
    subtitleVerbOpen: '回答待ち',
    subtitleVerbDismissed: '却下済み',
    subtitleVerbAnswered: '回答済み',

    openTabIntro:
      'ボットが答えられなかった質問をグループ化して表示しています。一度回答を書けばQ&Aソースになり、以後ずっとボットが覚えています。',

    emptyTitleOpen: '未対応の質問はありません — ボットはすべての質問に回答済みです。',
    emptyTitleDismissed: '却下した質問はありません。',
    emptyTitleAnswered: 'まだ回答済みの質問はありません。',

    askedOnce: '1回質問',
    askedTimes: ({ n }: { n: number }) => `${n}回質問`,

    viewSourceCta: 'ソースを見る',
    sourceRemoved: 'ソースは削除済み',
    viewChatCta: 'チャットを見る',
    chatDeleted: 'チャットは削除済み',

    dismissCta: '却下',
    reopenCta: '再オープン',
    writeAnswerCta: '回答を書く',
    cancelCta: 'キャンセル',
    answerPlaceholder: 'この訪問者に返す回答を書いてください。以後、ボットがこの内容を使用します。',
    addingCta: '追加中…',
    addAsQaSourceCta: 'Q&Aソースとして追加',
    answerAddedNotice: 'Q&Aソースとして追加しました — 次回の会話からボットが使用します。',
    saveAnswerError: 'この回答を保存できませんでした — もう一度お試しください。',
  },
  appearance: {
    pageTitle: '外観',
    pageSubtitle: 'サイト上でウィジェットがどう見え、訪問者にどう挨拶するか',

    colorLabel: '色',
    colorHexAriaLabel: 'カラーコード(16進数)',
    botNameLabel: 'ボット名',
    botNamePlaceholder: 'Acme サポート',
    welcomeMessageLabel: 'ウェルカムメッセージ',
    welcomePlaceholder: 'こんにちは!今日はどのようなご用件ですか?',

    quickQuestionsLabel: 'クイック質問',
    addQuestionCta: '質問を追加',
    quickQuestionsHint: '任意 — 訪問者が何も入力する前にタップできるチップとして表示されます。',
    quickQuestionAriaLabel: ({ n }: { n: number }) => `クイック質問 ${n}`,
    quickQuestionPlaceholder: '営業時間を教えてください',
    removeCta: '削除',

    positionLabel: '起動ボタンの位置',
    positionBottomRight: '右下',
    positionBottomLeft: '左下',

    languageLabel: '言語',
    autoLanguageLabel: '訪問者のブラウザに合わせる',

    badgeLabel: '「Powered by Embent」バッジ',
    badgeHint: 'ウィジェットのフッターに小さな「Powered by Embent」リンクを表示します。',
    badgeSwitchAriaLabel: '「Powered by Embent」バッジを表示',
    badgeLoadError: '読み込めませんでした — 更新してください',
    freeBadgeNote: ({ plans }: { plans: string }) => `${plans}にアップグレードすると、この表示義務はなくなります。`,

    saveCta: '保存',
    savingCta: '保存中…',
    savedLabel: '保存しました',

    livePreviewHeading: 'ライブプレビュー',
    previewCaption: 'ここには起動ボタンとパネルのみ表示されます — 周囲のページは意図的に空白にしています。',
    devHintPrefix: '何も表示されない場合、ウィジェットのバンドルがローカルでビルドされていません。実行:',
    devHintIn: '(場所:',
    devHintAndCopy: ') → コピー:',
    devHintTo: '→',

    widgetPreviewTitle: 'ウィジェットプレビュー',
  },
  botSettings: {
    pageTitle: '設定',
    pageSubtitle: '許可ドメイン、リード獲得、ボットの自己紹介の仕方',

    leadCaptureOffLabel: 'オフ',
    leadCaptureOffBlurb: 'ボットは訪問者に連絡先を尋ねません。',
    leadCaptureOnIntentLabel: '意図に応じて',
    leadCaptureOnIntentBlurb: 'ボットは、答えられない場合か訪問者がフォローアップを望む場合にのみ、メールアドレスや電話番号を尋ねます。',
    leadCaptureAlwaysLabel: '常時',
    leadCaptureAlwaysBlurb: 'ボットは会話の最初のやり取りの直後に、一度だけ連絡先を尋ねます。',
    leadCapturePreChatLabel: 'チャット前フォーム',
    leadCapturePreChatBlurb:
      'チャット開始前に、訪問者は簡単な連絡先フォーム(名前、メール、任意の電話番号)に入力します。チャット中にボットが改めて尋ねることはありません。',

    leadLevelSubtleLabel: '控えめ',
    leadLevelSubtleBlurb: '訪問者から連絡を希望されたり見積もりを求められたりしたときにのみ、フォローアップを提案します。',
    leadLevelBalancedLabel: 'バランス型(推奨)',
    leadLevelBalancedBlurb:
      '訪問者が実際に購買意欲を示したとき(始め方を尋ねる、デモを希望する、自分のサイトについて話すなど)に一度だけ提案します。',
    leadLevelProactiveLabel: '積極的',
    leadLevelProactiveBlurb: '訪問者が検討していそうなときはいつでも提案し、最初の提案に反応がなければ後でもう一度尋ねます。',

    domainRequiredError: '必須項目です — 空欄のままにせず、この行を削除してください',
    domainBareHostnameError: 'ホスト名のみを入力してください。例:「example.com」(スキームやパスは不要)',
    domainTooLongError: '長すぎます',

    aboutLabel: 'このボットについて',
    aboutHint:
      'ボットが常に知っている短い自己紹介です — 誰なのか、誰のためのものか。「あなたは誰ですか?」のような質問にも、' +
      '検索せずすぐに答えられるようになります。',
    aboutPlaceholder: 'Acmeのサポートアシスタントです。Acme Widgetsのセットアップやトラブル対応をお手伝いします。',

    allowedDomainsLabel: '許可ドメイン',
    addDomainCta: 'ドメインを追加',
    allowedDomainsHint:
      'このボットのウィジェットを埋め込めるサイトです。空欄のままにするとすべてのサイトで許可されます — 公開前に' +
      '自分のドメインだけに制限することをお勧めします。',
    domainAriaLabel: ({ n }: { n: number }) => `許可ドメイン ${n}`,
    domainPlaceholder: 'example.com',
    removeCta: '削除',

    leadCaptureLabel: 'リード獲得',
    leadLevelLabel: '積極度',

    saveCta: '保存',
    savingCta: '保存中…',
    savedLabel: '保存しました',

    embedSnippetHeading: '埋め込みスニペット',
    embedPastePrefix: 'これを閉じる',
    embedPasteSuffix: 'タグの直前にサイトに貼り付けてください。',

    embedCopyCta: 'コピー',
    embedCopiedCard: 'コピーしました!',
    embedCopiedCheck: '✓ コピー済み',

    dangerZoneHeading: '危険な操作',
    dangerZoneDescription: 'このボットと、そのソース、会話、リード、検索インデックスを削除します。この操作は元に戻せません。',
    deleteBotCta: 'このボットを削除',
    deleteBotDialogTitle: ({ name }: { name: string }) => `「${name}」を削除しますか?`,
    dangerZoneDialogDescriptionPrefix:
      'このボットと、そのソース、会話、リード、検索インデックスを削除します。この操作は元に戻せません。確認のため ',
    dangerZoneDialogDescriptionSuffix: ' と入力してください。',
    confirmBotNameAriaLabel: 'ボット名を確認',
    cancelCta: 'キャンセル',
    deletingCta: '削除中…',
  },
  notifications: {
    heading: '通知',
    subheading: 'メールは各メンバーのアカウントのアドレスに送信されます。Playgroundでの操作でメールが送られることはありません。',
    noMembers: 'メンバーがいません。',
    saveError: 'この設定を保存できませんでした。もう一度お試しください。',

    roleOwner: 'オーナー',
    roleAdmin: '管理者',
    roleMember: 'メンバー',
    cadenceOff: 'オフ',
    cadenceDaily: '毎日',
    cadenceWeekly: '毎週',

    colMember: 'メンバー',
    colNewLead: '新規リード',
    colUnansweredDigest: '未回答ダイジェスト',
    colCrawlFailed: 'クロール失敗',
    colWeeklySummary: '週次サマリー',

    newLeadAriaLabel: ({ email }: { email: string }) => `${email} 宛の新規リードメール`,
    unansweredDigestAriaLabel: ({ email }: { email: string }) => `${email} 宛の未回答ダイジェストメール`,
    crawlFailedAriaLabel: ({ email }: { email: string }) => `${email} 宛のクロール失敗メール`,
    weeklySummaryAriaLabel: ({ email }: { email: string }) => `${email} 宛の週次サマリーメール`,
  },
  playground: {
    pageTitle: 'Playground',
    pageSubtitle: '訪問者と同じようにボットとチャットできます — ここでの操作がサイトに影響することはありません。',
    retrievalDebugLabel: '検索デバッグ',
    hideDebugCta: 'デバッグを隠す',
    newSessionCta: '新しいセッション',
    emptyStateText: 'メッセージを送って、このボットがソースに基づいてどう回答するか試してみてください。',
    inputPlaceholder: 'このボットに質問する…',
    stopCta: '停止',
    sendCta: '送信',
    debugEmptyText: '質問すると、検索結果が表示されます',
    genericError: '問題が発生しました。',
  },
  billing: {
    pageTitle: '請求',
    openingCta: '開いています…',
    manageBillingCta: '請求を管理',

    memberGateTitle: '請求はワークスペースのオーナーまたは管理者が管理します。',
    memberGateDescription: 'このワークスペースのプランを変更するには、オーナーまたは管理者に依頼してください。',

    nowOnPlan: ({ name }: { name: string }) => `現在${name}プランをご利用中です。`,
    finishingUp: '処理を完了しています — プランが更新されない場合は、少し待ってから更新してください。',
    confirmingWithPolar: '変更を受け付けました — Polarで確認中…',

    internalWorkspaceLabel: '社内ワークスペース',
    monthlyPriceLabel: ({ price }: { price: number }) => `$${price} / 月`,
    internalWorkspaceInline: '上限なし、請求は発生しません。',
    internalWorkspaceHeading: '社内ワークスペース — 上限なし、請求は発生しません。',
    internalWorkspaceBody:
      'すべてのプラン上限が適用されず、ここでアップグレードやチェックアウトを行う必要もありません。利用状況は ' +
      '可視化のため引き続き記録されますが、Polarには送信されません — このワークスペースには請求先情報がありません。',

    unlimitedWord: '無制限',
    unlimitedParens: '(無制限)',
    ofCap: ({ cap }: { cap: string }) => `/ ${cap}`,
    internalRenewalFallback: '社内ワークスペース · 上限なし · 請求なし',
    capsSuffix: ({
      bots,
      messages,
      pages,
    }: {
      bots: string;
      botsCount: number | null;
      messages: string;
      pages: string;
    }) => `ボット${bots}台 · メッセージ${messages}件 · ページ${pages}`,
    freeForever: ({ capsSuffix }: { capsSuffix: string }) => `永久無料 · ${capsSuffix}`,
    renewsOn: ({ date, capsSuffix }: { date: string; capsSuffix: string }) => `更新日 ${date} · ${capsSuffix}`,

    cancelsAtPeriodEnd: '期間終了時に解約',
    planChangesPausedNotice: '現在の期間が終了するまで、プラン変更は一時停止されています。',
    planChangesPausedReason: 'このサブスクリプションは期間終了時に解約される設定です — プランを変更するには、まず解約を取り消してください。',

    redirectingCta: 'リダイレクト中…',
    switchingCta: '切り替え中…',
    upgradeToCta: ({ name }: { name: string }) => `${name}にアップグレード`,

    messagesThisMonthLabel: '今月のメッセージ',
    pagesIndexedLabel: 'インデックス済みページ',
    messageNoun: 'メッセージ',
    pageNoun: 'ページ',
    messagesUnit: ({ n }: { n: string }) => `${n}件のメッセージ`,
    usageLimitWarning: ({ pct, noun }: { pct: number; noun: string }) => `${noun}上限の${pct}%`,
    upgradeRaisesTo: ({ name, unit }: { name: string; unit: string }) => ` — ${name}なら${unit}まで増やせます。`,
    limitReached: 'に達しました。',

    cancelSubscriptionCta: 'サブスクリプションを解約',
    cancelingCta: '解約中…',
    cancelDialogTitle: 'サブスクリプションを解約しますか?',
    yourCurrentPlanFallback: '現在のプラン',
    cancelDialogDescription: ({
      planName,
      renewsDate,
    }: {
      planName: string;
      renewsDate: string | null;
    }) =>
      `ボットは今回の請求期間が終わるまで${planName}のまま動作し続けます${
        renewsDate ? `(${renewsDate})` : ''
      } — その後、ワークスペースはFreeに移行します。即座に解約されることはありません。`,
    keepSubscriptionCta: 'サブスクリプションを継続',

    changePlanFallbackTitle: 'プランを変更しますか?',
    keepCurrentPlanCta: '現在のプランを維持',
    confirmFallbackCta: '確認',

    checkoutErrorFallback: 'チェックアウトの開始中に問題が発生しました。',
    portalErrorFallback: '請求管理画面を開く際に問題が発生しました。',
    cancelErrorFallback: 'サブスクリプションの解約中に問題が発生しました。',

    currentPlanBadge: '現在のプラン',
    botsUnit: ({ n }: { n: number }) => `ボット${n}台`,
    seatsUnit: ({ n }: { n: number }) => `${n}シート`,
    messagesPerMonth: ({ n }: { n: string }) => `${n}メッセージ / 月`,
    pagesUnit: ({ n }: { n: string }) => `${n}ページ`,
    badgeRequiredText: '「Powered by Embent」バッジ',
    badgeOptionalText: '「Powered by Embent」バッジは任意',
    upgradeCta: 'アップグレード',
    switchToCta: ({ name }: { name: string }) => `${name}に切り替え`,

    flatPricingNote:
      '定額料金、シート数による計算なし、想定外の超過料金なし — 80%で通知し、110%を超えた場合のみ新しい' +
      '回答を一時停止します。回答の途中で会話を打ち切ることは決してありません。',

    planChangeConfirm: {
      verbUpgrade: 'アップグレード',
      verbSwitch: '切り替え',
      title: ({ verb, name }: { verb: string; name: string }) => `${name}へ${verb}しますか?`,
      confirmNow: ({ verb }: { verb: string }) => `今すぐ${verb}`,
      continueToPolarCta: 'Polarへ進む',
      checkoutDescription: ({ name, price }: { name: string; price: number }) =>
        `Polarに移動して${name}サブスクリプション($${price}/月)を開始します — 有効化のための請求はPolar側で行われます。`,
      switchDescription: ({ name, price }: { name: string; price: number }) =>
        `プランはすぐに切り替わります — ボットはただちに${name}の上限($${price}/月)を使い始めます。本日Polarから請求されることはなく、日割り差額は次回請求書に反映されます。`,
    },
  },
  workspace: {
    pageTitle: 'ワークスペース設定',
    loadErrorMessage: 'このワークスペースを読み込めませんでした。ページを再読み込みしてもう一度お試しください。',
    notAllowedTitle: 'ワークスペース設定を変更できるのは、オーナーと管理者のみです。',
    notAllowedDescription: '変更するには、このワークスペースのオーナーまたは管理者に依頼してください。',

    nameLabel: 'ワークスペース名',
    nameHint: ({ min, max }: { min: number; max: number }) =>
      `${min}〜${max}文字。このワークスペースの全員と招待メールに表示されます。`,

    uploadLogoCta: 'ロゴをアップロード',
    removeLogoCta: '削除',
    logoHint: '正方形が最適です。PNG、JPEG、WebP、GIFに対応、最大2MB。',
    logoNotConfiguredError: 'この環境ではロゴのアップロードが設定されていません。',
    uploadLogoErrorFallback: 'ロゴをアップロードできませんでした。もう一度お試しください。',
    removeLogoErrorFallback: 'ロゴを削除できませんでした。もう一度お試しください。',

    saveNameErrorFallback: '名前を保存できませんでした。もう一度お試しください。',
    saveCta: '保存',
    savedLabel: '保存しました',

    cropDialogTitle: 'ロゴをトリミング',
    cropDialogDescription: 'ドラッグして位置を調整し、ズームでサイズを合わせてください。256×256の正方形として保存されます。',
    zoomLabel: 'ズーム',
    cropErrorFallback: 'この画像をトリミングできませんでした。別のファイルをお試しください。',
    cancelCta: 'キャンセル',
    useThisLogoCta: 'このロゴを使用',
  },
  members: {
    pageTitle: 'メンバー',
    membersOnlyTitle: 'メンバー専用エリア',
    membersOnlyDescription: 'メンバーの管理は、ワークスペースのオーナーまたは管理者に依頼してください。',
    unlimitedCapWord: '無制限',
    seatsSubtitle: ({
      used,
      cap,
      pending,
    }: {
      used: number;
      cap: string | number;
      pending: number;
    }) => `${used}/${cap}シート使用中${pending > 0 ? `(招待中${pending}件)` : ''}`,
    inviteMemberCta: 'メンバーを招待',

    roleOwner: 'オーナー',
    roleAdmin: '管理者',
    roleMember: 'メンバー',
    roleOptionMember: 'メンバー',
    roleOptionAdmin: '管理者',

    colName: '名前',
    colEmail: 'メールアドレス',
    colRole: '役割',
    leaveWorkspaceCta: 'ワークスペースを退出',
    removeCta: '削除',

    pendingInvitesHeading: '招待中',
    noPendingInvites: '招待中のものはありません。',
    inviteColEmail: 'メールアドレス',
    inviteColRole: '役割',
    inviteColExpires: '有効期限',
    revokingCta: '取り消し中…',
    revokeCta: '取り消し',

    cancelCta: 'キャンセル',
    removingCta: '削除中…',
    leaveDialogTitle: 'このワークスペースを退出しますか?',
    removeDialogTitle: ({ name }: { name: string }) => `${name}を削除しますか?`,
    leaveDialogDescription: 'すぐにアクセス権を失います — オーナーまたは管理者が後から再招待できます。',
    removeDialogDescription: ({ name }: { name: string }) => `${name}はこのワークスペースへのアクセス権をただちに失います。`,

    emailLabel: 'メールアドレス',
    emailPlaceholder: 'teammate@example.com',
    roleLabel: '役割',
    inviteErrorFallback: 'この招待の送信中に問題が発生しました。',
    sendingInviteCta: '送信中…',
    sendInviteCta: '招待を送信',

    roleChangeErrorFallback: 'この役割を変更できませんでした。',
    removeErrorFallback: 'このメンバーを削除できませんでした。',
    revokeErrorFallback: 'この招待を取り消せませんでした。',
  },
  account: {
    pageTitle: 'アカウント',
    loadSignInMethodsError: 'サインイン方法を読み込めませんでした',
    emailPasswordLabel: 'メールアドレスとパスワード',
    googleLabel: 'Google',

    savingCta: '保存中…',

    profileHeading: 'プロフィール',
    displayNameLabel: '表示名',
    namePlaceholder: 'お名前',
    emailLabel: 'メールアドレス',
    saveNameErrorFallback: '名前の保存中に問題が発生しました。',
    saveCta: '保存',
    savedLabel: '保存しました',

    passwordHeading: 'パスワード',
    passwordMismatchError: '新しいパスワードと確認用パスワードが一致しません。',
    currentPasswordLabel: '現在のパスワード',
    newPasswordLabel: '新しいパスワード',
    confirmNewPasswordLabel: '新しいパスワード(確認)',
    changePasswordErrorFallback: 'パスワードの変更中に問題が発生しました。',
    changePasswordCta: 'パスワードを変更',
    passwordChangedLabel: 'パスワードを変更しました',
  },
  adminImpersonate: {
    impersonateFailedFallback: 'このユーザーになりすませませんでした。',
    lookupFailedFallback: 'このユーザーを検索できませんでした。',
    userNotFound: 'ユーザーアカウントが見つかりません。',
    noOwnerToView: 'このワークスペースには閲覧対象となるオーナーがいません。',
  },
  admin: {
    pagination: {
      pageOfTotal: ({
        page,
        totalPages,
        totalCount,
      }: {
        page: number;
        totalPages: number;
        totalCount: string;
      }) => `${totalPages}ページ中${page}ページ目 · 全${totalCount}件`,
      prevCta: '前へ',
      nextCta: '次へ',
    },
    workspaces: {
      crawlSignalRenderedLabel: 'レンダリングモードのクロールへの依存度',
      crawlSignalSourcesAcrossBots: ({
        botCountLabel,
      }: {
        sourceCount: number;
        botCountLabel: string;
        botCount: number;
      }) => `件のソース(ボット${botCountLabel}台にわたる)`,
      crawlSignalRenderedFootnote:
        'Cloudflare Browser Renderingにどれだけ依存しているか — 現在Workers Freeプランで得ている容量であり、Paidプランのものではありません。',
      crawlSignalCapacityLabel: '自社のレンダリング容量上限に到達(過去7日間)',
      // `n` only drove English's singular/plural branch; Japanese has none, so the param is
      // unused but kept in the signature to match `en.ts`'s function shape exactly.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      crawlSignalSourceCountLabel: ({ n }: { n: number }) => `件のソース`,
      crawlSignalCapacityFootnote:
        '重複のないソース数で、あくまで自社側の容量の話です — 顧客のプラン上限とは無関係です。強制終了されたプロセスや回収された古いジョブは記録に残らないため、これは下限値です。継続的に増加している場合は、Workers Paid($5/月)への移行のタイミングです。',

      emptyText: 'まだワークスペースがありません。',
      internalBadge: '社内',
      cancelsAtPeriodEndHint: '期間終了時に解約',
      colName: '名前',
      colOwner: 'オーナー',
      colPlan: 'プラン',
      colBots: 'ボット',
      colMembers: 'メンバー',
      colMsgsPerMo: 'メッセージ/月',
      colPages: 'ページ',
      colCreated: '作成日',
      colActions: '操作',
      noOwnerTitle: 'このワークスペースにはオーナーがいません',
      viewAsCta: '閲覧',
      switchingCta: '切り替え中…',
      viewAsErrorFallback: '失敗しました',

      backToWorkspaces: '← ワークスペース一覧に戻る',
      ownerLabel: 'オーナー: ',
      bannedBadge: '停止中',
      createdLabel: ({ date }: { date: string }) => `作成日 ${date}`,
      usageHeading: '利用状況',
      messagesThisMonthLabel: '今月のメッセージ',
      pagesIndexedLabel: 'インデックス済みページ',
      botsLabel: 'ボット',
      internalUsageNote: '社内ワークスペース — 上限は適用されません。利用状況は引き続き上に記録されます。',
      botsHeading: 'ボット',
      noBotsYet: 'まだボットがありません。',
      colBotName: '名前',
      colPublicId: '公開ID',
      colStatus: 'ステータス',
      colBotCreated: '作成日',
      membersHeading: 'メンバー',
      noMembersYet: 'メンバーがいません。',
      colEmail: 'メールアドレス',
      colMemberName: '名前',
      colRole: '役割',
      colJoined: '参加日',
      memberRoleLabel: {
        owner: 'オーナー',
        admin: '管理者',
        member: 'メンバー',
      },
      subscriptionHeading: 'サブスクリプション',
      statusLabel: 'ステータス: ',
      periodEndLabel: '期間終了日: ',
      cancelsAtPeriodEndLabel: '期間終了時に解約: ',
      yesLabel: 'はい',
      noLabel: 'いいえ',
      polarSubscriptionIdLabel: 'Polarサブスクリプション ID: ',
      manageInPolarLink: 'Polarで管理 ↗',
      noSubscriptionText: 'サブスクリプションなし — このワークスペースはFreeプランです。',
      dangerZoneHeading: '危険な操作',
      internalToggleLabel: '社内ワークスペース',
      internalToggleHint: '上限なし、請求なし — 利用状況は記録されますが、Polarには送信されません。',
      banOwnerLabel: 'オーナーを停止',
      banOwnerHint: '停止されたユーザーはサインインできません。ウィジェットや公開APIには影響しません。',
      unbanCta: '停止解除',
      unbanningCta: '停止解除中…',
      banCta: '停止',
      demoteBeforeBanTitle: '停止する前に、この管理者を一般ユーザーに降格してください。',
      roleLabel: '役割',
      roleHint: '管理者はすべての社内管理画面にアクセスできます。',
      cannotChangeOwnRoleTitle: '自分自身の役割は変更できません。',
      cancelCta: 'キャンセル',
      savingCta: '保存中…',
      confirmCta: '確認',
      markInternalDialogTitle: 'このワークスペースを社内扱いにしますか?',
      removeInternalDialogTitle: '社内扱いを解除しますか?',
      markInternalDialogDescription:
        'このワークスペースは、すべてのプラン上限(ボット、メッセージ、ページ)を無視し、請求が発生することはありません。利用状況は可視化のため引き続き記録されますが、夜間のPolar利用状況送信の対象からは除外されます — 送信先となる請求先情報がないためです。',
      removeInternalDialogDescription: 'このワークスペースは通常のプラン制限と請求(有効なサブスクリプションがある場合)に戻ります。',
      banDialogTitle: ({ email }: { email: string | undefined }) => `${email} を停止しますか?`,
      banDialogDescription: 'これによりただちにサインアウトされ、停止解除まで再度サインインできなくなります。',
      banReasonLabel: '理由(任意)',
      banReasonPlaceholder: 'このアカウントを停止する理由は?',
      banningCta: '停止処理中…',
      banUserCta: 'ユーザーを停止',
      roleDialogTitle: ({ role }: { role: 'user' | 'admin' | null }) => `役割を${role}に変更しますか?`,
      roleDialogDescriptionAdmin: ({ email }: { email: string | undefined }) =>
        `${email} はすべての社内管理画面にアクセスできるようになります。`,
      roleDialogDescriptionUser: ({ email }: { email: string | undefined }) =>
        `${email} は社内管理画面にアクセスできなくなります。`,
      updateWorkspaceErrorFallback: 'このワークスペースを更新できませんでした。',
      banUserErrorFallback: 'このユーザーを停止できませんでした。',
      unbanUserErrorFallback: 'このユーザーの停止を解除できませんでした。',
      changeRoleErrorFallback: 'このユーザーの役割を変更できませんでした。',
    },
    bots: {
      colName: '名前',
      colPublicId: '公開ID',
      colWorkspace: 'ワークスペース',
      colStatus: 'ステータス',
      colSources: 'ソース',
      colLastCrawl: '最終クロール',
      colInstanceId: 'インスタンスID',
      noBotsYet: 'まだボットがありません。',

      backToBots: '← ボット一覧に戻る',
      workspaceLabel: 'ワークスペース: ',
      instanceLabel: ({ id }: { id: string }) => `インスタンス: ${id}`,
      createdLabel: ({ date }: { date: string }) => `作成日 ${date}`,
      openOwnerViewCta: 'オーナーの画面を開く',
      openingCta: '開いています…',
      openOwnerViewErrorFallback: 'オーナーの画面を開けませんでした。',
      sourcesHeading: 'ソース',
      noSourcesYet: 'まだソースがありません。',
      colUrl: 'URL',
      colType: '種類',
      colSourceStatus: 'ステータス',
      colPages: 'ページ',
      colLastCrawled: '最終クロール',
      colError: 'エラー',
      recentJobsHeading: '最近のジョブ',
      noJobsLinked: 'このボットに紐づくジョブはまだありません。',
      colKind: '種別',
      colJobStatus: 'ステータス',
      colAttempts: '試行回数',
      colUpdated: '更新日時',
      colPayload: 'ペイロード',
      recentJobsFootnote:
        '最近のジョブは、それぞれのペイロードをもとにベストエフォートで照合されています — このボットのIDやそのソースIDを含まないジョブは、間接的にこのボットに関係していても、ここには表示されません。',
    },
    jobs: {
      tabLabel: {
        all: 'すべて',
        running: '実行中',
        failed: '失敗',
      },
      noFailedJobs: '失敗したジョブはありません — キューは正常です。',
      noJobsToShow: '表示するジョブがありません。',
      colKind: '種別',
      colStatus: 'ステータス',
      colPayload: 'ペイロード',
      colAttempts: '試行回数',
      colCreated: '作成日時',
      colUpdated: '更新日時',
      colActions: '操作',
      retryCta: '再試行',
      retryingCta: '再試行中…',
      cancelCta: 'キャンセル',
      cancellingCta: 'キャンセル中…',
      cancelJobCta: 'ジョブをキャンセル',
      keepJobCta: 'ジョブを維持',
      cancelDialogTitle: 'このジョブをキャンセルしますか?',
      retryJobErrorFallback: 'このジョブを再試行できませんでした。',
      cancelJobErrorFallback: 'このジョブをキャンセルできませんでした。',
      cancelDialogDescriptionListRow:
        'この保留中のジョブ行を削除します — 二度と実行されません。この方法でキャンセルできるのは保留中のジョブのみです。実行中のジョブはすでにハンドラーが処理を進めている可能性があります。',

      backToJobs: '← ジョブ一覧に戻る',
      jobIdLabel: 'ジョブID',
      attemptsLabel: '試行回数',
      createdLabel: '作成日時',
      updatedLabel: '更新日時',
      runAfterLabel: '実行予定',
      errorHeading: 'エラー',
      payloadHeading: 'ペイロード',
      cancelDialogDescriptionDetailPage: 'この保留中のジョブ行を削除します — 二度と実行されません。',
    },
    posts: {
      description: 'ブログ記事、下書きと公開済み。ここでの編集は公開から1分以内にembent.comに反映されます。',
      newPostCta: '新規投稿',
      emptyText: 'まだ投稿がありません — 最初の投稿を作成しましょう。',
      colTitle: 'タイトル',
      colStatus: 'ステータス',
      colAuthor: '著者',
      colPublished: '公開日',
      colUpdated: '更新日',
      scheduledLabel: ({ date }: { date: string }) => `予約公開 · ${date}`,
    },
    postEditor: {
      backToPosts: '← 投稿一覧',
      newPostTitle: '新規投稿',
      editPostTitleFallback: '投稿を編集',

      titleLabel: 'タイトル',
      titlePlaceholder: 'Embentがサポートの質問にどう答えるか',
      slugLabel: 'スラッグ',
      slugPlaceholder: 'how-embent-answers-support-questions',
      slugUrlPrefix: 'embent.com/blog/',
      excerptLabel: '抜粋',
      excerptPlaceholder: '1〜2文で — /blogページに表示され、メタディスクリプションとしても使われます。',
      tagsLabel: 'タグ',
      tagsPlaceholder: 'rag, support, launch',
      tagsHint: 'カンマ区切りで入力してください。',
      coverUrlLabel: 'カバー画像URL',
      coverUrlPlaceholder: 'https://cdn.embent.com/blog/…',

      authorLabel: '著者',
      editAuthorCta: '著者を編集',
      newAuthorCta: '+ 新規著者',
      authorSelectPlaceholder: '著者を選択',
      noAuthorsYet: 'まだ著者がいません — 上の「+ 新規著者」から追加してください。',

      visibilityScheduledText: ({ date }: { date: string }) => `${date}に公開予定`,
      visibilityLiveText: ({ date }: { date: string }) => `${date}に公開済み`,
      visibilityDraftText: '未公開',
      publishDateLabel: '公開日時',
      publishDateHintPrefix: 'タイムゾーン: ',
      publishDateHintSuffix:
        '(このブラウザの時刻)。空欄のままにすると、公開した瞬間に公開されます。未来の時刻を設定すると、その時刻になるまで投稿は非表示になります — ブログ、タグ件数、サイトマップ、RSSフィードから、その時刻になるまで表示されません。表示させるために何かを実行しておく必要はありません。',

      bodyLabel: '本文(Markdown)',
      livePreviewLabel: 'ライブプレビュー',

      missingFieldLabel: {
        title: 'タイトル',
        slug: 'スラッグ',
        excerpt: '抜粋',
        body: '本文',
        author: '著者',
      },
      beforeSavingAdd: ({ fields }: { fields: string }) => `保存する前に、次を追加してください: ${fields}。`,

      savingCta: '保存中…',
      saveChangesCta: '変更を保存',
      saveDraftCta: '下書きを保存',
      unpublishingCta: '非公開処理中…',
      schedulingCta: '予約設定中…',
      publishingCta: '公開中…',
      unpublishCta: '非公開にする',
      scheduleCta: '予約公開',
      publishCta: '公開',
      savedFlash: '保存しました',

      renderingPreview: 'レンダリング中…',
      previewFailedPrefix: 'プレビューを読み込めませんでした: ',

      uploadFailedFallback: 'アップロードに失敗しました',
      bodyPlaceholder: 'Markdownで書いてください…画像を貼り付けるかドラッグすると、本文内にアップロードされます。',
      uploadingText: 'アップロード中…',
      imageUploadFailedPrefix: '画像のアップロードに失敗しました: ',

      deleteCta: '削除',
      deleteDialogTitle: ({ title }: { title: string }) => `「${title}」を削除しますか?`,
      deleteDialogDescriptionPrefix: 'この投稿を完全に削除します。この操作は元に戻せません。確認のため',
      deleteDialogDescriptionSuffix: ' と入力してください。',
      confirmPostTitleAriaLabel: '投稿タイトルを確認',
      cancelCta: 'キャンセル',
      deletingCta: '削除中…',
      deletePostCta: '投稿を削除',

      editAuthorDialogTitle: ({ name }: { name: string }) => `${name}を編集`,
      newAuthorDialogTitle: '新規著者',
      authorDialogDescription: '実在する著者のみ — 架空の人物は不可です。',
      authorNameLabel: '名前',
      authorNamePlaceholder: 'Johnny Bui',
      authorRoleLabel: '役職',
      authorRolePlaceholder: '創業者',
      authorAvatarLabel: 'アバターURL',
      authorAvatarPlaceholder: 'https://cdn.embent.com/…',
      authorBioLabel: '経歴',
      authorPersonaLabel: 'ペルソナ(内部用)',
      authorPersonaPlaceholder: '短く直接的な文章を書く。誇張には懐疑的。形容詞より具体例を好む。',
      authorPersonaHint: 'この著者の文体で執筆するための内部メモです — 公開されず、読者には表示されません。',
      authorSavingCta: '保存中…',
      authorAddingCta: '追加中…',
      saveAuthorCta: '著者を保存',
      addAuthorCta: '著者を追加',

      writingGuideHeading: '執筆ガイド',
      writingGuideSubtitle: 'Markdown対応、画像、AI用ブリーフィングプロンプト',
      supportedMarkdownHeading: '対応しているMarkdown記法',
      mdListHeadings: '見出し(レベル1〜6)',
      mdListLists: '箇条書きと番号付きリスト',
      mdListLinksPrefix: 'リンク — ',
      mdListLinksSuffix: '、http(s)とmailtoのみ',
      mdListImages: '本文中どこにでも画像を挿入可能(記事の途中も含む) — 貼り付けまたはドラッグで追加(下記参照)',
      mdListCode: 'インラインおよびフェンス付きコードブロック(シンタックスハイライト対応)',
      mdListTables: 'テーブル(GitHub形式、列の配置対応)',
      mdListBlockquotes: '引用ブロック',
      strippedHeading: '削除される内容',
      strippedPrefix: 'それ以外はすべて削除されます。生のHTMLタグ、および',
      strippedSuffix:
        ' の内容は特に、決して残りません — 本文はどこに表示される場合も(このプレビューを含む)、固定の許可リスト(見出し、リスト、リンク、画像、コード、テーブル、引用ブロック、およびそれらを構成する段落・強調)に基づいてサニタイズされます。',
      imagesHeading: '画像',
      imagesPrefix: '本文に画像を貼り付けるかドラッグしてください — cdn.embent.comにアップロードされ、',
      imagesSuffix:
        ' の形でカーソル位置に挿入されるので、段落の途中にも配置できます。上のカバー画像は別のフィールドで、本文に貼り付けた画像から自動的に設定されることはありません。',
      copiedCta: 'コピーしました!',
      copyAiPromptCta: 'AIプロンプトをコピー',
      copiedToClipboard: 'クリップボードにコピーしました。',
      clipboardUnavailable: 'このブラウザではクリップボードが使用できません — 下のプロンプトを選択してコピーしてください。',
      assemblesPrefix: 'ハウススタイル、',
      assemblesAuthorPersonaClause: ({ authorName }: { authorName: string }) => `${authorName}のペルソナ`,
      assemblesNoAuthorClause: '著者が未選択であることのメモ',
      assemblesSuffix: '、上記のMarkdown記法、そして指定した構成をまとめます — 外部のAIモデルに貼り付けて、できた下書きを本文に貼り戻してください。',

      // aiPrompt* below is the literal content assembled into the "Copy AI prompt" clipboard
      // text — it drafts an English blog post (blog posts are English-only in every locale,
      // per the translator contract §6 and `blog.englishOnlyNote`), so it stays in English
      // regardless of dashboard locale; only the surrounding `assembles*` UI copy above is
      // translated.
      aiPromptPersonaWithVoice: ({ authorName, persona }: { authorName: string; persona: string }) =>
        `Write in ${authorName}'s voice:\n${persona}`,
      aiPromptPersonaNoPersona: ({ authorName }: { authorName: string }) =>
        `${authorName} has no persona set — write in a plain, direct voice.`,
      aiPromptAuthorFallbackLower: 'this author',
      aiPromptAuthorFallbackUpper: 'This author',
      aiPromptTemplate: ({ personaSection }: { personaSection: string }) =>
        `You are drafting a blog post for Embent (embent.com), an AI agent platform for websites.

House style:
- Direct and concrete. No hype, no marketing fluff.
- Short paragraphs (2-4 sentences).
- Never invent statistics or customer names. If you don't have a real number or a real customer, don't make one up.

Author voice:
${personaSection}

Markdown conventions (this is ALL that will render — everything else is stripped):
- Headings (#, ##, ###), bulleted and numbered lists, blockquotes.
- Links as [text](url) — http(s) and mailto only.
- Images anywhere in the body, including mid-article, as ![alt](url).
- Inline and fenced code blocks.
- Tables (GitHub-flavored).
- Raw HTML and <script>/<style> tags are stripped entirely — write plain Markdown only, no HTML.

Requested structure:
1. Title
2. One-paragraph hook
3. 4-7 sections, each with an h2 (##) heading
4. A short closing

Write the full draft now as Markdown.`,
    },
    feedback: {
      filterLabel: {
        unhandled: '未対応',
        all: 'すべて',
      },
      typeLabel: {
        bug: '不具合',
        feature: '機能',
        question: '質問',
      },
      mailtoSubject: ({ id, type }: { id: string; type: string }) => `Re: [Embent feedback ${id}] ${type}`,
      replyCta: '返信',
      colDate: '日付',
      colWorkspace: 'ワークスペース',
      colUser: 'ユーザー',
      colType: '種類',
      colMessage: 'メッセージ',
      colActions: '操作',
      emptyUnhandled: '未対応のフィードバックはありません — 受信箱ゼロです。',
      emptyAll: 'まだフィードバックがありません。',
      updateFeedbackErrorFallback: 'このフィードバックを更新できませんでした。',
      reopeningCta: '再オープン中…',
      markingCta: '処理中…',
      reopenCta: '再オープン',
      markHandledCta: '対応済みにする',
      noBotFallback: 'ボットなし',
      noUaFallback: 'UAなし',
    },
    playground: {
      demosAllTimeLabel: 'デモ(全期間)',
      demos30dLabel: 'デモ · 30日間',
      messagesAllTimeLabel: 'メッセージ(全期間)',
      signedUpAllTimeLabel: '登録済み',
      signedUp30dLabel: '登録済み · 30日間',
      leadsAllTimeLabel: '獲得したリード',
      leads30dLabel: 'リード · 30日間',
      demosPerDayLabel: '1日あたりのデモ数',
      messagesPerDayLabel: '1日あたりのメッセージ数',
      demoUnit: (): string => 'デモ',
      messageUnit: (): string => 'メッセージ',
      noDemos30d: '過去30日間のデモはありません。',
      statusBreakdownHeading: 'ステータス内訳(30日間)',
      recentDomainsHeading: '最近のドメイン',
      noDemosYet: 'まだデモがありません。',
      colHostname: 'ホスト名',
      colStatus: 'ステータス',
      colPages: 'ページ',
      colMessages: 'メッセージ',
      colAttempts: '試行回数',
      colSignedUp: '登録済み',
      colLeads: 'リード',
      colCreated: '作成日',
      signedUpAriaLabel: '登録済み',
      notSignedUpAriaLabel: '未登録',
      noEmailFallback: '(メールなし)',
    },
  },
} satisfies Catalog;
